/**
 * BFF (Backend-for-Frontend) auth on QTP: cookie-based, no browser token storage.
 *
 * Cookie: __Host-app_session on the API host (api.sandbox.mechagora.net / api.mechagora.com).
 * Login: GET {apiBase}/auth/start → IdP → GET {apiBase}/auth/callback → Set-Cookie → return_to SPA.
 *
 * Do NOT use the portal host as apiBase — __Host- cookies are host-only and cannot be set on
 * sandbox.mechagora.net for the api.sandbox host. Browser must call the QTP edge with
 * credentials: 'include'.
 *
 * Sandbox default when env is empty: https://api.sandbox.mechagora.net
 */

const SANDBOX_QTP_BFF = 'https://api.sandbox.mechagora.net';

/**
 * Resolve QTP BFF base for /auth/start and /session/me.
 * Prefer NEXT_PUBLIC_QTP_BFF_URL, then NEXT_PUBLIC_API_BASE_URL when it points at api.*,
 * else sandbox edge default in browser.
 */
export function resolveQtpBffBase(explicit?: string): string {
  if (explicit && explicit.trim()) {
    return explicit.replace(/\/$/, '');
  }
  if (typeof process !== 'undefined') {
    const qtp =
      process.env.NEXT_PUBLIC_QTP_BFF_URL ||
      process.env.NEXT_PUBLIC_QTP_AUTH_URL ||
      '';
    if (qtp.trim()) return qtp.replace(/\/$/, '');
    const api = process.env.NEXT_PUBLIC_API_BASE_URL || '';
    // Only use public API base when it is the QTP edge (not empty same-origin portal BFF).
    if (api && /api\.(sandbox\.)?mechagora\.(net|com)/i.test(api)) {
      return api.replace(/\/$/, '');
    }
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host.includes('sandbox.mechagora.net') || host.includes('localhost')) {
      return SANDBOX_QTP_BFF;
    }
    if (host.endsWith('mechagora.com') || host.endsWith('mechagora.net')) {
      return 'https://api.mechagora.com';
    }
  }
  return SANDBOX_QTP_BFF;
}

/** Absolute SPA return_to so QTP callback redirects to the portal, not the API host. */
function absoluteReturnTo(returnTo?: string): string {
  if (typeof window === 'undefined') {
    return returnTo && returnTo.startsWith('/') ? returnTo : returnTo || '/';
  }
  const path = returnTo || window.location.pathname || '/';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${p}`;
}

export const BFF_AUTH = {
  /**
   * Login: redirect to BFF /auth/start. Optional return_to (allowlisted on server: CORS SPA hosts).
   */
  loginUrl(apiBase: string, returnTo?: string): string {
    const base = resolveQtpBffBase(apiBase);
    const url = `${base}/auth/start`;
    if (returnTo) {
      return `${url}?return_to=${encodeURIComponent(returnTo)}`;
    }
    return url;
  },

  /**
   * Navigate to BFF login (use from Login button).
   * return_to is absolute portal origin so callback lands on the SPA.
   */
  redirectToLogin(apiBase: string, returnTo?: string): void {
    window.location.href = BFF_AUTH.loginUrl(
      apiBase,
      absoluteReturnTo(returnTo || window.location.pathname || '/')
    );
  },

  /**
   * Step-up re-auth URL: max_age=0 and return_to (for tests and redirect).
   * Prefer absolute SPA return_to from redirectToStepUp so callback hits the portal.
   */
  stepUpUrl(apiBase: string, returnTo?: string): string {
    const base = resolveQtpBffBase(apiBase);
    const path = returnTo ?? '/';
    return `${base}/auth/start?max_age=0&return_to=${encodeURIComponent(path)}`;
  },

  /**
   * Step-up re-auth: redirect with max_age=0 so IdP forces fresh login.
   */
  redirectToStepUp(apiBase: string, returnTo?: string): void {
    window.location.href = BFF_AUTH.stepUpUrl(
      apiBase,
      absoluteReturnTo(returnTo || window.location.pathname || '/')
    );
  },

  /**
   * Fetch current session from BFF. Use credentials: 'include' so __Host-app_session is sent.
   */
  async getSessionMe(
    apiBase: string
  ): Promise<{ user_id: string; email?: string; scopes?: string; auth_time?: number } | null> {
    const base = resolveQtpBffBase(apiBase);
    const res = await fetch(`${base}/session/me`, { credentials: 'include' });
    if (res.status === 401) return null;
    if (!res.ok) return null;
    return res.json();
  },

  /**
   * Logout: POST /auth/logout with credentials, then clear local state.
   */
  async logout(apiBase: string): Promise<void> {
    const base = resolveQtpBffBase(apiBase);
    await fetch(`${base}/auth/logout`, { method: 'POST', credentials: 'include' });
  },
};
